import { Header } from "../components/Header";
import { BreadCrumb } from "../components/BreadCrumb";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  getBooks,
  getBookRecords,
  joinClass,
  getStudentClasses,
  getChapters,
  deleteCorrectionTicket,
} from "../services/api";
import { useCorrectionStatus, selectTicketsForView, type CorrectionTicket } from "../hooks/useCorrectionStatus";
import { takeSubmitError, quotaState, filterRecordsByClass } from "../utils/uploadQuota";
import { Book } from "../components/Book";
import type { Book as BookType } from "../types/common";
import { Footer } from "../components/Footer";
import { Button, TextField, Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import DescriptionIcon from "@mui/icons-material/Description";
import type { Record , classDataProps, Chapter} from "../types/common";
// import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
// import { setResult } from "../utils/resultProcess"; // 不再使用
import { getUserData } from "../utils/userData";
// import { useAuth } from "../hooks/useAuth";
import Swal from "sweetalert2";
import { JoinSuccessModal } from "../components/JoinSuccessModal";
import { Slider } from "../components/Slider";
import { BookCarousel } from "../components/BookCarousel";
import { classesForBook, classBelongsToBook } from "../data/examBlock";
import type { EmblaOptionsType } from "embla-carousel";
import { timeProcess } from "../utils/timeProcess";
import {
  parseCorrectingResult,
  extractTotalScore,
} from "../utils/correctingResultGuard";

// 一筆後端紀錄 → 列表列(kanban 1000412):初載與批改完成後重載兩處共用。
// correctingResult 解析失敗、或解得開卻取不到總分 → 標 parseError,
// 分數欄顯示紅字「批改失敗」—— 過去這裡給 score:'0',學生會誤以為考了 0 分。
const toStudentRecordRow = (item: Record): Record => {
  const createdAt = timeProcess(item.createdAt);
  const parsed = parseCorrectingResult(item?.correctingResult);
  const score = parsed.ok ? extractTotalScore(parsed.value) : undefined;
  return score === undefined
    ? { ...item, createdAt, parseError: true }
    : { ...item, createdAt, score };
};

export const StudentHome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [books, setBooks] = useState<BookType[]>([]);
  const [selectBookId, setSelectBookId] = useState<string>("");
  const [records, setRecords] = useState<Record[]>([]);
  // 學生在本書所有班級的紀錄總數：額度與後端同口徑（kanban 1000873）；null = 讀不到／載入中
  const [bookTotal, setBookTotal] = useState<number | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  
  const [joinClassModalOpen, setJoinClassModalOpen] = useState<boolean>(false);
  const [joinClassData, setJoinClassData] = useState({
    classCode: "",
    seatNo: "",
  });
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [joinSuccessModalOpen, setJoinSuccessModalOpen] = useState<boolean>(false);
  const [joinSuccessData, setJoinSuccessData] = useState({
    bookName: "",
    className: "",
    seatNo: "",
    newClassId: ""
  });
  
  // Add loading states to prevent flickering
  const [isLoadingBooks, setIsLoadingBooks] = useState<boolean>(true);
  const [isLoadingClasses, setIsLoadingClasses] = useState<boolean>(true);
  const [isLoadingRecords, setIsLoadingRecords] = useState<boolean>(true);
  
  // 使用 useMemo 來穩定用戶資料
  const userData = useMemo(() => getUserData(), []);
  const { name, email, sub } = userData;

  // 使用 useRef 來保持最新的狀態值，避免閉包問題
  const selectBookIdRef = useRef(selectBookId);
  const classesRef = useRef(classes);
  const selectedClassIdRef = useRef(selectedClassId);
  // 用於取消過時的 API 請求，避免切換書籍時 race condition
  const loadRecordsRequestIdRef = useRef(0);

  // 更新 ref 值
  useEffect(() => {
    selectBookIdRef.current = selectBookId;
    classesRef.current = classes;
    selectedClassIdRef.current = selectedClassId;
  }, [selectBookId, classes, selectedClassId]);

  // 當切換書籍時，自動選擇該書籍的第一個班級
  useEffect(() => {
    if (selectBookId && Array.isArray(classes)) {
      // 學測/模考專區沿用陪練國寫班級(kanban 107204)
      const bookClasses = classesForBook(classes, selectBookId);
      if (bookClasses.length > 0) {
        // 如果當前選中的班級不屬於新書籍，自動選擇第一個
        const currentClassBelongsToBook = bookClasses.some(
          (cls: classDataProps) => cls._id === selectedClassId
        );
        if (!currentClassBelongsToBook) {
          setSelectedClassId(bookClasses[0]._id);
        }
      } else {
        // 如果新書籍沒有班級，重置 selectedClassId
        setSelectedClassId("");
      }
    }
  }, [selectBookId, classes]);

  // 使用批改狀態 hook - 暫時停用
  const {
    correctionTickets,
    failedTickets,
    recentlyCompletedTickets,
    isProcessing
  } = useCorrectionStatus({
    studentId: sub,
    enablePolling: true, // 重新啟用輪詢
    onCompleted: () => {
      // 有剛完成的票據，重新載入記錄以顯示新的批改結果
      setTimeout(() => {
        // 使用最新的狀態值來呼叫 reloadRecords
        reloadRecordsWithParams(selectBookIdRef.current, selectedClassIdRef.current, classesRef.current);
      }, 1000); // 延遲1秒後重新載入
    }
  });

  // 使用參數的重新載入記錄函數（避免閉包問題）
  const reloadRecordsWithParams = useCallback(async (
    bookId: string, 
    currentSelectedClassId: string, 
    currentClasses: any[]
  ) => {
    if (!bookId || !sub) {
      return;
    }
    // 與下方 effect 共用同一個計數器，過時的回應不得覆寫 records / bookTotal
    const currentRequestId = ++loadRecordsRequestIdRef.current;
    setIsLoadingRecords(true);
    
    // 獲取當前選中班級的 classCode
    let targetClassId = currentSelectedClassId;
    
    // 如果沒有選中的班級ID，則使用第一個班級作為默認值
    if (!targetClassId && currentClasses.length > 0) {
      targetClassId = currentClasses[0]._id;
    }
    
    const selectedClass = currentClasses.find(cls => cls._id === targetClassId);
    const classCode = selectedClass?.classCode;
    
    try {
      // 一次撈本書全部紀錄：總數給額度用、列表只留目前班級（kanban 1000873）
      const [chaptersRes, recordsRes] = await Promise.all([
        getChapters(bookId, sub).catch(() => ({ data: [] })),
        getBookRecords(bookId, sub)
      ]);
      if (currentRequestId !== loadRecordsRequestIdRef.current) return;
      
      // 處理章節資料
      const chaptersData = Array.isArray(chaptersRes.data) ? chaptersRes.data : [];
      setChapters(chaptersData);
      
      // 處理記錄資料(解析失敗標 parseError 顯示「批改失敗」,kanban 1000412)
      if (Array.isArray(recordsRes.data)) {
        setBookTotal(recordsRes.data.length);
        const result = filterRecordsByClass(recordsRes.data, classCode).map(toStudentRecordRow);

        setRecords(result);
      } else {
        setBookTotal(null);
        setRecords([]);
      }
    } catch (error) {
      if (currentRequestId !== loadRecordsRequestIdRef.current) return;
      console.error('重新載入記錄失敗:', error);
      setBookTotal(null);
    } finally {
      setIsLoadingRecords(false);
    }
  }, [sub]);



  // 檢查用戶登入狀態
  useEffect(() => {
    const checkLogin = () => {
      const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
      
      if (!userDataString) {
        navigate("/");
        return;
      }

      try {
        JSON.parse(userDataString);
        // const userData = JSON.parse(userDataString);
        // const userRole = userData.profile?.role || userData.profile?.['custom:role'] || "學生";
      } catch (error) {
        console.error("解析用戶資料失敗:", error);
        navigate("/");
      }
    };

    checkLogin();
  }, [navigate]);




  // const [expiredAt, setExpiredAt] = useState<null | string>(null);

  // 處理重試失敗的票據
  const handleRetryTicket = async (ticket: CorrectionTicket) => {
    // 重試要回到票原本那本書、那個班（kanban 1000823）；舊票缺欄位才退回當下選的
    const retryBookId = ticket.bookID || selectBookId;
    try {
      // 先刪除失敗的票據
      await deleteCorrectionTicket(ticket.ticket);
      
      // 導航到上傳頁面，帶上章節資訊
      const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
      const selectedClass = classes.find(cls => cls._id === currentSelectedClassId);
      const classCode = ticket.classCode || selectedClass?.classCode || "";
      const className = selectedClass?.className || "";
      
      let uploadUrl = `/upload-essay?bookId=${retryBookId}&classCode=${classCode}&className=${encodeURIComponent(className)}`;
      if (ticket.chapter) {
        // 嘗試找到對應的章節ID
        const matchingChapter = chapters.find(ch => ch.chapter === ticket.chapter);
        if (matchingChapter) {
          uploadUrl += `&chapterId=${matchingChapter._id}`;
        }
      }
      
      navigate(uploadUrl);
    } catch (error) {
      console.error('❌ 重試失敗票據失敗:', error);
      // 即使刪除失敗也導航到上傳頁面
      const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
      const selectedClass = classes.find(cls => cls._id === currentSelectedClassId);
      const classCode = ticket.classCode || selectedClass?.classCode || "";
      const className = selectedClass?.className || "";
      navigate(`/upload-essay?bookId=${retryBookId}&classCode=${classCode}&className=${encodeURIComponent(className)}`);
    }
  };


  const handleViewRecord = async (record: Record) => {
    // 檢查是否為剛完成的記錄（需要高亮顯示）
    const isRecentlyCompleted = recentlyCompletedTickets.includes(record.ticket || '');
    
    // 只有當記錄還在高亮狀態（剛完成）且有 ticket 時才刪除
    if (record.ticket && isRecentlyCompleted) {
      try {
        await deleteCorrectionTicket(record.ticket);
        // hook 會自動處理狀態更新
      } catch (error) {
        console.error('❌ 刪除批改 ticket 失敗:', error);
        // 即使刪除失敗也繼續檢視記錄
      }
    }

    // 如果是新完成的記錄，標記為已讀
    if (record.isNewlyCompleted) {
      // 清除最新完成記錄標記
      try {
        const latestCompletedId = localStorage.getItem('latestCompletedRecord');
        if (latestCompletedId === record._id) {
          localStorage.removeItem('latestCompletedRecord');
        }
      } catch (error) {
        console.error("移除新完成記錄標記失敗:", error);
      }
      
      // 更新記錄狀態，移除高亮
      const updatedRecords = records.map((r: Record) => {
        if (r._id === record._id) {
          return { ...r, isNewlyCompleted: false };
        }
        return r;
      });
      setRecords(updatedRecords);
    }
    
    // 將紀錄資料存儲到 sessionStorage 以供 Feedback 頁面使用
    const uploadMode = record.studentInputUrl !== "" ? "image" : "text";
    
    // 從 classes 中尋找 record 所屬班級:紀錄上有 classCode 就精確配對(學生可能同時
    // 在多個班);舊紀錄沒有 classCode 時退回「班級屬於該書且為目前選中的班級」。
    // 書籍歸屬用 classBelongsToBook:學測/模考書沿用陪練國寫舊班級,班級的 bookID
    // 不等於紀錄的 bookID,直接比對會永遠 match 不到 → classInfo 為 null、
    // 班級名/學生 email 丟失(kanban 113525)
    const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
    const bookClassData = Array.isArray(classes)
      ? classes.filter((item: classDataProps) =>
          item &&
          item.bookID &&
          (record.classCode
            ? item.classCode === record.classCode
            : classBelongsToBook(item, record.bookID) &&
              item._id === currentSelectedClassId)
        )
      : [];
    
    // 取得第一個匹配的班級資訊
    const classInfo = bookClassData.length > 0 ? bookClassData[0] : null;
    
    // 構建 workflowResult 格式，模擬 aiCorrecting API 的回應
    const workflowResult = {
      bookID: record.bookID,
      chapterID: record.chapterID,
      studentID: record.studentID,
      studentName: classInfo?.studentName || record.studentName, // 優先使用班級中的姓名
      studentEmail: classInfo?.studentEmail || "", // 使用班級中的 email
      studentSeatNo: classInfo?.studentSeatNo || record.studentSeatNo || "0", // 優先使用班級中的座號
      studentSeatNoInt: classInfo?.studentSeatNoInt || 0,
      className: classInfo?.className || "", // 加入班級名稱
      classCode: classInfo?.classCode || "", // 加入班級代碼
      studentInput: record.studentInput,
      studentInputUrl: record.studentInputUrl,
      studentInputOriginUrl: record.studentInputOriginUrl,
      correctingResult: record.correctingResult,
      _id: record._id,
      createdAt: record.createdAt,
      // 如果記錄中有 markdown 字段，也包含進去
      ...(record.markdown && { markdown: record.markdown })
    };
    
    // 存儲到 sessionStorage
    sessionStorage.setItem("workflowResult", JSON.stringify(workflowResult));
    sessionStorage.setItem("uploadMode", uploadMode);
    
    if (uploadMode === "text") {
      sessionStorage.setItem("essayText", record.studentInput);
    }
    
    // 設置學生檢視記錄模式
    sessionStorage.setItem("viewMode", "studentRecord");
    
    // 導航到正確的批改結果頁面
    navigate("/feedback");
  };

  const handleJoinSuccessConfirm = () => {
    // 選中剛加入的班級
    setSelectedClassId(joinSuccessData.newClassId);
  };

  const handleJoinClass = () => {
    joinClass({
      _id: sub + "_" + joinClassData.classCode,
      classCode: joinClassData.classCode,
      studentID: sub,
      studentEmail: email,
      studentName: name,
      studentSeatNo: joinClassData.seatNo,
      studentSeatNoInt: parseInt(joinClassData.seatNo),
    })
      .then(() => {
        setJoinClassModalOpen(false);
        getStudentClasses(sub).then((res) => {
          if (Array.isArray(res.data)) {
            setClasses(res.data);
            // 如果是第一次加入班級，選中第一個班級
            if (!selectedClassId && res.data.length > 0) {
              setSelectedClassId(res.data[0]._id);
            }
            
            // 找到剛加入的班級資訊
            const newClass = res.data.find((cls: any) => cls.classCode === joinClassData.classCode);
            if (newClass) {
              // 找到對應的書籍名稱
              const book = books.find(b => b._id === newClass.bookID);
              const bookName = book?.bookDesc || "未知書籍";
              
              // 設置成功模態的資料
              setJoinSuccessData({
                bookName,
                className: newClass.className,
                seatNo: joinClassData.seatNo,
                newClassId: newClass._id
              });
              
              // 顯示自定義成功模態
              setJoinSuccessModalOpen(true);
            } else {
              // 找不到班級資訊時的預設提示
              Swal.fire({
                icon: "success",
                title: "加入成功",
                timer: 1500,
                showConfirmButton: false,
              });
            }
          }
        }).catch(() => {});
        setJoinClassData({
          classCode: "",
          seatNo: "",
        });
      })
      .catch((err) => {
        if (err.response.data.detail === "您已經加入該班級") {
          Swal.fire({
            icon: "error",
            title: "已加入該班級",
            text: "請勿重複加入",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "加入失敗",
            text: "請確認邀請碼是否正確",
          });
        }
      });
  };

  useEffect(() => {
    if (!sub) {
      return;
    }
    
    // Load books and classes in parallel to minimize loading time
    Promise.all([
      getBooks().catch((err) => {
        console.error('getBooks 失敗:', err);
        return { data: [] };
      }),
      getStudentClasses(sub).catch((err) => {
        console.error('getStudentClasses 失敗:', err);
        return { data: [] };
      })
    ]).then(([booksRes, classesRes]) => {
      // Handle books data
      const booksData = Array.isArray(booksRes.data) && !booksRes.data.includes?.('<!doctype html>') 
        ? booksRes.data 
        : [];
      
      setBooks(booksData);
      setIsLoadingBooks(false);
      
      if (booksData.length > 0) {
        setSelectBookId(booksData[0]._id);
      }
      
      // Handle classes data
      const classesData = Array.isArray(classesRes.data) && !classesRes.data.includes?.('<!doctype html>') 
        ? classesRes.data 
        : [];
      setClasses(classesData);
      setIsLoadingClasses(false);
    }).catch((err) => {
      console.error('Promise.all 失敗:', err);
      setBooks([]);
      setClasses([]);
      setIsLoadingBooks(false);
      setIsLoadingClasses(false);
    });
  }, [sub]);

  // 單獨處理班級預設選擇，避免循環依賴
  useEffect(() => {
    if (classes.length > 0 && !selectedClassId) {
      setSelectedClassId(classes[0]._id);
    }
  }, [classes, selectedClassId]);

  const quota = quotaState(bookTotal, books.find((book) => book._id === selectBookId)?.usedLimit);

  // 上傳頁是先跳回這裡、再送出；送出被後端拒絕時只有這裡能顯示原因（kanban 1000873）。
  // 載入時查一次、之後每次收到上傳頁的通知再查一次；取走即清，同一個原因只跳一次。
  useEffect(() => {
    const showPendingSubmitError = () => {
      const message = takeSubmitError();
      if (!message) return;
      Swal.fire({
        icon: "error",
        title: "作文沒有送出",
        text: message,
        confirmButtonText: "確定",
      });
      // 會被擋通常代表畫面上的總數已落後（否則按鈕早就停用了）：重抓一次，
      // 讓「本書已用」與上傳鈕停用跟上真實狀態，不然學生會再按一次、再被擋一次
      reloadRecordsWithParams(selectBookIdRef.current, selectedClassIdRef.current, classesRef.current);
    };
    showPendingSubmitError();
    window.addEventListener("recordStatusUpdate", showPendingSubmitError);
    return () => window.removeEventListener("recordStatusUpdate", showPendingSubmitError);
  }, [reloadRecordsWithParams]);

  // 上傳後導回：依網址上的 bookId／classCode 選到上傳那本書、那個班（kanban 1000823）。
  // 列表預設是第一本書，而批改中列只在上傳那本書底下顯示，不選對書會什麼都看不到。
  // 書與班在同一個 effect 處理、只改一次網址，避免兩個 effect 各自 navigate 互相蓋掉。
  useEffect(() => {
    const fromUpload = searchParams.get('fromUpload');
    if (!fromUpload || books.length === 0 || classes.length === 0) return;

    const bookId = searchParams.get('bookId');
    const classCode = searchParams.get('classCode');
    if (bookId) {
      const targetBook = books.find((book) => book._id === bookId);
      if (targetBook && targetBook._id !== selectBookId) {
        setSelectBookId(targetBook._id);
      }
    }
    if (classCode) {
      const targetClass = classes.find(cls => cls.classCode === classCode);
      if (targetClass && targetClass._id !== selectedClassId) {
        setSelectedClassId(targetClass._id);
      }
    }

    // 清除 URL 參數
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('bookId');
    newSearchParams.delete('classCode');
    newSearchParams.delete('fromUpload');
    navigate({ search: newSearchParams.toString() }, { replace: true });
  }, [searchParams, books, classes, selectBookId, selectedClassId, navigate]);

  useEffect(() => {

    if (selectBookId && sub) {
      // 遞增 requestId，讓過時的請求回來時可以忽略
      const currentRequestId = ++loadRecordsRequestIdRef.current;
      setIsLoadingRecords(true);
      // 切書時先把總數設回未知，不拿上一本書的數字對新書的上限做停用判斷
      setBookTotal(null);

      // 獲取當前選中班級的 classCode
      const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
      const selectedClass = classes.find((cls: classDataProps) => cls._id === currentSelectedClassId);
      const classCode = selectedClass?.classCode;


      Promise.all([
        getChapters(selectBookId, sub).catch(() => {
          // Chapter loading failed but continue with records
          return { data: [] }; // 返回空章節資料
        }),
        getBookRecords(selectBookId, sub)
      ]).then(([chaptersRes, recordsRes]) => {
        // 如果已經有更新的請求，忽略這次過時的回應
        if (currentRequestId !== loadRecordsRequestIdRef.current) return;

        // 處理章節資料
        const chaptersData = Array.isArray(chaptersRes.data) ? chaptersRes.data : [];
        setChapters(chaptersData);

        // 處理記錄資料
        const res = recordsRes;

        if (Array.isArray(res.data)) {
          // 直接在這裡處理資料，保持原始 chapterID 用於比對
          // (解析失敗標 parseError 顯示「批改失敗」,kanban 1000412)
          setBookTotal(res.data.length);
          const result = filterRecordsByClass(res.data, classCode).map(toStudentRecordRow);
          setRecords(result);
        } else {
          setBookTotal(null);
          setRecords([]);
        }
        setIsLoadingRecords(false);
      }).catch((error) => {
        // 如果已經有更新的請求，忽略這次過時的回應
        if (currentRequestId !== loadRecordsRequestIdRef.current) return;
        console.error('Failed to load records:', error);
        setBookTotal(null);
        setRecords([]);
        setChapters([]);
        setIsLoadingRecords(false);
      });
    }
      
      // 設置過期時間 - 已註解掉，不再使用
      // const expiredAt = books.find((book) => book._id === selectBookId)?.expiredAt as Date;
      // if (expiredAt) {
      //   const date = new Date(expiredAt);
      //   const year = date.getFullYear();
      //   const month = String(date.getMonth() + 1).padStart(2, "0");
      //   const day = String(date.getDate()).padStart(2, "0");
      //   setExpiredAt(`${year}年${month}月${day}日`);
      // }
  }, [selectBookId, sub, books, selectedClassId, classes]);
  const SLIDE_COUNT = books.length;
  const SLIDES = Array.from(Array(SLIDE_COUNT).keys());
  const OPTIONS: EmblaOptionsType = { slidesToScroll: "auto" };
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header
        joinClassModalOpen={joinClassModalOpen}
        setJoinClassModalOpen={setJoinClassModalOpen}
      />
      
      {/* 麵包屑導航 */}
      <div className="md:px-xl px-sm max-w-2xl mx-auto w-full">
        <div className="flex items-center py-4">
          <BreadCrumb breadCrumb="/student-home" />
        </div>
        <hr className="text-black/20" />
      </div>
      
      <div className="md:px-xl px-sm py-md flex-1 max-w-2xl mx-auto w-full">
        <div className="flex flex-col gap-6 w-full">
          {/* 書籍 */}
          <section className="mx-2">
            {isLoadingBooks ? (
              <div className="flex items-center justify-center w-full py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-gray-600">載入書籍中...</span>
              </div>
            ) : (
              <>
                <Slider
                  slides={SLIDES}
                  options={OPTIONS}
                  booksData={books}
                  selectBookId={selectBookId}
                  setSelectBookId={setSelectBookId}
                  classData={classes}
                />
                <BookCarousel scrollToIndex={books.findIndex((book: BookType) => book?._id === selectBookId)}>
                  {Array.isArray(books) && books.map((book: BookType) => {
                    // 確保 book 和 book._id 存在
                    if (!book || !book._id) return null;

                    const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
                    // 同 handleViewRecord:學測/模考書沿用舊班級,書籍歸屬用
                    // classBelongsToBook 而非 bookID 相等(kanban 113525)
                    const classData = Array.isArray(classes)
                      ? classes.filter((item: classDataProps) =>
                          item &&
                          item.bookID &&
                          classBelongsToBook(item, book._id) &&
                          item._id === currentSelectedClassId
                        )
                      : [];
                    return (
                      <Book
                        key={book._id}
                        book={book}
                        selectBookId={selectBookId}
                        setSelectBookId={setSelectBookId}
                        classData={classData}
                      />
                    );
                  })}
                </BookCarousel>
              </>
            )}
          </section>

          {/* 操作提醒 */}
          <div className="mx-2 px-4 py-3 bg-[#E8F4FC] border border-secondary rounded-lg flex items-center gap-2">
            <span className="text-lg flex-shrink-0">👉</span>
            <span className="text-primary">
              操作提醒：每本書籍對應不同邀請碼，請確認已開通並切至目標書籍後，再上傳作文，以確保老師能收到您的成績。
            </span>
          </div>

          {/* 班級頁籤 */}
          {(() => {
            const filteredClasses = Array.isArray(classes)
              ? classesForBook(classes, selectBookId)
              : [];
            return filteredClasses.length > 0 && (
              <div className="h-9 w-fit" style={{ marginBottom: '-1.3rem' }}>
                {filteredClasses.map((classItem, index) => (
                  <span
                    key={classItem._id || index}
                    onClick={() => setSelectedClassId(classItem._id)}
                    className={`py-2 px-4 w-fit rounded-t-base cursor-pointer relative top-2 mr-1 z-10 ${
                      selectedClassId === classItem._id || (selectedClassId === "" && index === 0)
                        ? "bg-[#1876d2] text-white"
                        : "bg-white text-black/25"
                    }`}
                    style={{
                      borderTop: '1px solid rgba(0, 0, 0, 0.2)',
                      borderLeft: '1px solid rgba(0, 0, 0, 0.2)',
                      borderRight: '1px solid rgba(0, 0, 0, 0.2)',
                      borderBottom: 'none'
                    }}
                  >
                    {classItem.className}
                  </span>
                ))}
              </div>
            );
          })()}
          
          {/* 批改紀錄 */}
          <section className="border border-black/20 rounded-tr-base rounded-br-base rounded-bl-base bg-white flex-1 p-8">
            {(() => {
              // 過濾出屬於當前書籍的班級(學測/模考專區含陪練國寫舊班級)
              const currentBookClasses = Array.isArray(classes)
                ? classesForBook(classes, selectBookId)
                : [];

              if (isLoadingClasses) {
                return (
                  /* Loading state */
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-lg text-gray-600">載入中...</p>
                  </div>
                );
              }

              if (currentBookClasses.length === 0) {
                return (
                  /* 未加入此書籍班級時的提示 */
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-xl font-semibold text-gray-800 mb-6">
                      需加入班級才可上傳作文，請向老師索取邀請碼
                    </p>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setJoinClassModalOpen(true)}
                      sx={{ fontSize: "16px", px: 4, py: 1.5 }}
                    >
                      加入班級
                    </Button>
                  </div>
                );
              }

              return (
              <>
                {/* 功能按鍵 */}
                <div className="flex justify-between items-center">
                  <p className="flex gap-2 md:text-lg">
                    <span>本書已用</span>
                    <span className="text-red-500">{quota.used ?? "-"}</span>
                    <span>/</span>
                    <span>{quota.limit}</span>
                    <span>篇</span>
                  </p>
                  <div style={{ display: 'inline-block' }}>
                    <Tooltip 
                      title={
                        isProcessing
                          ? "評改中，請等待完成後再上傳新的作文"
                          : quota.reached
                            ? `已達本書上限 ${quota.limit} 篇`
                            : ""
                      }
                      arrow
                      placement="top"
                    >
                      <span>
                        <Button
                          onClick={() => {
                            const selectedBook = books.find(
                              (book) => book._id === selectBookId
                            );
                            if (selectedBook) {
                              // 獲取當前選中班級的資訊
                              const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
                              const selectedClass = classes.find(cls => cls._id === currentSelectedClassId);
                              const classCode = selectedClass?.classCode || "";
                              const className = selectedClass?.className || "";
                              
                              
                              navigate(
                                `/upload-essay?bookName=${selectedBook.bookDesc}&bookId=${selectBookId}&classCode=${classCode}&className=${encodeURIComponent(className)}`
                              );
                            }
                          }}
                          sx={{ fontSize: "md:18px" }}
                          variant="contained"
                          disabled={isProcessing || quota.reached}
                        >
                          上傳作文
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                </div>
                {/* <h2>批改紀錄</h2> */}
                <table className="w-full md:mt-6 mt-4 bg-gray-100 rounded-lg shadow-md overflow-hidden">
                  <thead className="bg-[#1876d2] md:text-lg text-nowrap">
                    <tr>
                      <th className="md:px-4 px-2 py-2 text-white">題目</th>
                      <th className="md:px-4 px-2 py-2 text-white">分數</th>
                      <th className="md:px-4 px-2 py-2 text-white">上傳時間</th>
                      <th className="md:px-4 px-2 py-2 text-white">批改細節</th>
                    </tr>
                  </thead>
                  <tbody className="">
                    {/* 顯示批改中的票據 */}
                    {(() => {
                      const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
                      const selectedClass = classes.find((cls: any) => cls._id === currentSelectedClassId);
                      const currentClassCode = selectedClass?.classCode;

                      // log 票據資料，確認是否有 bookID
                      console.log("correctionTickets 完整資料:", correctionTickets);
                      console.log("當前選擇的 bookId:", selectBookId);

                      // 篩選當前班級＋當前書籍的票據（kanban 1000823：學測／模考沿用班級碼，只比班級碼會兩本書都顯示）
                      const filteredTickets = selectTicketsForView(correctionTickets, {
                        classCode: currentClassCode,
                        bookId: selectBookId,
                      });

                      return filteredTickets;
                    })().map((ticket: any) => (
                      <tr key={ticket._id} className="border-b border-gray-300 text-center md:text-base text-sm bg-yellow-100">
                        <td className="md:px-4 px-2 py-2">
                          <span className="line-clamp-2">
                            {ticket.chapter || '未知章節'}
                          </span>
                        </td>
                        <td className="md:px-4 px-2 py-2">-</td>
                        <td className="md:px-4 px-2 py-2">-</td>
                        <td className="md:px-4 px-2 py-2">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span>批改中，請稍後回來查看</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {/* 顯示失敗的票據（與批改中列同一套過濾：當前班級＋當前書籍，kanban 1000823） */}
                    {(() => {
                      const currentSelectedClassId = selectedClassId || (classes.length > 0 ? classes[0]._id : "");
                      const selectedClass = classes.find((cls: any) => cls._id === currentSelectedClassId);
                      return selectTicketsForView(failedTickets, {
                        classCode: selectedClass?.classCode,
                        bookId: selectBookId,
                      });
                    })().map((ticket: CorrectionTicket) => (
                      <tr key={ticket._id} className="border-b border-gray-300 text-center md:text-base text-sm bg-red-50">
                        <td className="md:px-4 px-2 py-2">
                          <span className="line-clamp-2">
                            {ticket.chapter || '未知章節'}
                          </span>
                        </td>
                        <td className="md:px-4 px-2 py-2">-</td>
                        <td className="md:px-4 px-2 py-2">-</td>
                        <td className="md:px-4 px-2 py-2">
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-red-600 text-sm">{ticket.status}</span>
                            <Button
                              sx={{
                                fontSize: "14px",
                                padding: "4px 12px",
                              }}
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleRetryTicket(ticket)}
                            >
                              重試
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {/* 然後顯示一般 records */}
                    {Array.isArray(records) && records.length > 0 ? (
                      records.map((record: Record, index: number) => {
                        // 確保 record 存在且有必要的屬性
                        if (!record || !record.createdAt) return null;
                        
                        // 檢查是否為已完成的 ticket，需要高亮顯示
                        const isRecentlyCompleted = recentlyCompletedTickets.includes(record.ticket || '');
                        
                        return (
                        <tr key={record._id || index} className={`border-b border-gray-300 text-center md:text-base text-sm ${
                          isRecentlyCompleted
                            ? "bg-green-100 border-green-300"
                            : record.isNewlyCompleted
                            ? "bg-yellow-50"
                            : "hover:bg-gray-50"
                        }`}>
                          <td className="md:px-4 px-2 py-2">
                            <span className="line-clamp-2">
                              {(() => {
                                // 確保章節數據和記錄章節ID存在
                                if (!Array.isArray(chapters) || !record.chapterID) {
                                  return record.chapterID || '未知章節';
                                }
                                
                                // 嘗試用 _id 比對
                                let chapterInfo = chapters.find(chapter => chapter && chapter._id === record.chapterID);
                                
                                // 如果用 _id 找不到，嘗試用 chapter 標題比對
                                if (!chapterInfo) {
                                  chapterInfo = chapters.find(chapter => chapter && chapter.chapter === record.chapterID);
                                }
                                
                                // 如果還是找不到，嘗試部分匹配
                                if (!chapterInfo) {
                                  chapterInfo = chapters.find(chapter => 
                                    chapter && chapter.chapter && record.chapterID &&
                                    (record.chapterID.includes(chapter.chapter) || 
                                    chapter.chapter.includes(record.chapterID))
                                  );
                                }
                                
                                return chapterInfo ? chapterInfo.chapter : record.chapterID;
                              })()}
                            </span>
                          </td>
                          <td className="md:px-4 px-2 py-2">
                            {/* 解析失敗筆顯示「批改失敗」(kanban 1000412),
                                不再顯示 0 分讓學生誤以為考 0 分 */}
                            {record.parseError ? (
                              <span
                                className="text-red-600"
                                title="AI 回傳的批改結果格式錯誤，請重新上傳批改"
                              >
                                批改失敗
                              </span>
                            ) : (
                              record.score
                            )}
                          </td>
                          <td className="md:px-4 px-2 py-2">
                            <div className="flex justify-center items-center gap-1 md:flex-row flex-col">
                              <span className="text-wrap">
                                {(() => {
                                  // 處理不同的時間格式
                                  const createdAt = record.createdAt;
                                  if (createdAt.includes("T")) {
                                    // ISO 格式: 2025-07-21T09:24:12.781000
                                    const date = new Date(createdAt);
                                    return date.toLocaleDateString('zh-TW', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit'
                                    }).replace(/\//g, '/');
                                  } else {
                                    // 原本格式: 2025/07/21 09:24
                                    return createdAt.split(" ")[0];
                                  }
                                })()}
                              </span>
                              <span className="text-wrap">
                                {(() => {
                                  // 處理不同的時間格式
                                  const createdAt = record.createdAt;
                                  if (createdAt.includes("T")) {
                                    // ISO 格式: 2025-07-21T09:24:12.781000
                                    const date = new Date(createdAt);
                                    return date.toLocaleTimeString('zh-TW', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false
                                    });
                                  } else {
                                    // 原本格式: 2025/07/21 09:24
                                    return createdAt.split(" ")[1] || "";
                                  }
                                })()}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            {record.parseError ? (
                              /* 解析失敗筆不給檢視（kanban 1000412，與老師端一致）：
                                 handleViewRecord 會對壞字串再 JSON.stringify 造成
                                 雙重序列化污染，細節頁也解不開 */
                              <span
                                className="text-gray-400 font-medium"
                                title="AI 回傳的批改結果格式錯誤，無法檢視紀錄"
                              >
                                無法檢視
                              </span>
                            ) : (
                              <Button
                                sx={{
                                  fontSize: "16px",
                                  padding: "2px 8px",
                                  border: "none",
                                }}
                                variant="outlined"
                                color="primary"
                                startIcon={<DescriptionIcon />}
                                onClick={() => handleViewRecord(record)}
                              >
                                <span className="md:inline hidden">檢視紀錄</span>
                              </Button>
                            )}
                          </td>
                        </tr>
                        );
                      })
                    ) : isLoadingRecords ? (
                      <tr className="h-40">
                        <td
                          className="text-center text-2xl text-black/24"
                          colSpan={4}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            <span>載入中...</span>
                          </div>
                        </td>
                      </tr>
                    ) : Array.isArray(records) && records.length === 0 ? (
                      <tr className="h-40">
                        <td
                          className="text-center text-2xl text-black/24"
                          colSpan={4}
                        >
                          此書籍尚無批改紀錄
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </>
              );
            })()}
          </section>
          {/* <p className="text-red-500 justify-center flex items-center gap-2 md:text-base text-sm">
            <ErrorOutlineIcon />
            <span>
              {expiredAt
                ? `貼心提醒：本書所附的 AI 批改服務將提供至 ${expiredAt}。書籍改版後將同步更換，敬請把握`
                : "貼心提醒：翰林提供此 AI 批改服務時間為兩年"}
            </span>
          </p> */}
        </div>
      </div>
      <Footer />
      {joinClassModalOpen && (
        <div
          onClick={() => {
            setJoinClassModalOpen(false);
            setJoinClassData({
              classCode: "",
              seatNo: "",
            });
          }}
          className="fixed top-0 left-0 w-screen h-screen bg-black/20 z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-14 rounded-lg shadow-lg flex flex-col gap-y-4 sm:min-w-120 min-w-80"
          >
            <h2 className="text-lg font-semibold">加入班級</h2>
            <TextField
              label="邀請碼"
              variant="outlined"
              value={joinClassData.classCode}
              onChange={(e) => {
                const value = e.target.value;
                // 只允許數字，最多8位
                if (/^\d{0,8}$/.test(value)) {
                  setJoinClassData({
                    ...joinClassData,
                    classCode: value,
                  });
                }
              }}
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <TextField
              label="我的座號"
              variant="outlined"
              value={joinClassData.seatNo}
              onChange={(e) => {
                const value = e.target.value;
                // 只允許數字，最多6位
                if (/^\d{0,6}$/.test(value)) {
                  setJoinClassData({ 
                    ...joinClassData, 
                    seatNo: value 
                  });
                }
              }}
              className="w-full p-2 border border-gray-300 rounded mb-4"
            />
            <Button
              disabled={
                joinClassData.classCode === "" || joinClassData.seatNo === ""
              }
              onClick={handleJoinClass}
              variant="contained"
              color="primary"
            >
              加入
            </Button>
          </div>
        </div>
      )}
      
      <JoinSuccessModal
        open={joinSuccessModalOpen}
        onClose={() => setJoinSuccessModalOpen(false)}
        bookName={joinSuccessData.bookName}
        className={joinSuccessData.className}
        seatNo={joinSuccessData.seatNo}
        onConfirm={handleJoinSuccessConfirm}
      />
    </div>
  );
};

export default StudentHome;